import { makeid } from 'utils/helpers/makeid';

import {
  nurseContactWithPatientItem,
  nurseDecisionMakingNextStepsItem,
  nurseDigitalEngagementEducationMethodItem,
  nurseEmergencyCareAdvisedItem,
  nurseFollowUpPlanItem,
  nurseMaTasksDelegatedItem,
  nurseNoCallMadeRationaleItem,
  nursePatientAcknowledgementItem,
  nursePatientAgreeabilityItem,
  nurseReviewEscalationProtocolItem,
} from '../common';

import type { TFormItem } from '../../types';

export const weightClinicalAssessmentList = [
  {
    title: 'Rapid weight gain (≥ __ lbs/kg in 1-2 days)',
    name: 'rapidWeightGain',
  },
  {
    title: 'Rapid weight loss (≥ __ lbs/kg in 1-2 days)',
    name: 'rapidWeightLoss',
  },
  {
    title: 'Swelling in legs/feet/abdomen',
    name: 'swellingInLegsFeetAbdomen',
  },
  {
    title: 'Shortness of breath—new or worsening',
    name: 'shortnessOfBreathNewOrWorsening',
  },
  {
    title: 'Difficulty breathing when lying down (orthopnea)',
    name: 'difficultyBreathing',
  },
  {
    title: 'Awakening short of breath (paroxysmal nocturnal dyspnea)',
    name: 'awakeningShortOfBreath',
  },
  { title: 'Fatigue', name: 'fatigue' },
  {
    title: 'Dizziness or lightheadedness',
    name: 'dizzinessLightheadedness',
  },
  {
    title: 'Confusion/mental status change',
    name: 'confusionMentalStatusChange',
  },
  { title: 'Chest pain/pressure', name: 'chestPainPressure' },
  { title: 'Frequent urination', name: 'frequentUrination' },
  {
    title: 'Nausea, decreased appetite',
    name: 'nauseaDecreasedAppetite',
  },
  {
    title: 'Mild swelling/edema',
    name: 'mildSwellingEdema',
  },
  {
    title: 'Mild difficulty breathing with exertion',
    name: 'mildDifficultyBreathing',
  },
  //
  {
    title: 'Number of pillows used for sleep increased',
    name: 'numberPillowsUsedForSleepIncreased',
  },
  {
    title: 'Medication adherence (especially diuretics)',
    name: 'medicationAdherence',
  },
  {
    title: 'Dietary sodium/fluid intake changes',
    name: 'dietarySodiumFluidIntakeChanges',
  },
  {
    title: 'Recent physical activity changes',
    name: 'recentPhysicalActivityChanges',
  },
  {
    title: 'Recent illness/infection',
    name: 'recentIllnessInfection',
  },
];

const yesNoClinicalAssessment = [
  'numberPillowsUsedForSleepIncreased',
  'medicationAdherence',
  'dietarySodiumFluidIntakeChanges',
  'recentPhysicalActivityChanges',
  'recentIllnessInfection',
];

const clinicalAssessmentItems: TFormItem[] = weightClinicalAssessmentList.map(
  (item) => {
    const presentDeniedOptions = [
      { label: 'Present', value: true, key: makeid(10) },
      { label: 'Denied', value: false, key: makeid(10) },
    ];
    const yesNoOptions = [
      { label: 'Yes (describe)', value: true, key: makeid(10) },
      { label: 'No', value: false, key: makeid(10) },
    ];
    return {
      key: makeid(10),
      title: <p>{item.title}:</p>,
      type: 'radio',
      name: item.name,
      required: false,
      options: yesNoClinicalAssessment.includes(item.name)
        ? yesNoOptions
        : presentDeniedOptions,
    };
  },
);

export const weight: TFormItem[] = [
  {
    key: makeid(10),
    title: <p>Chart Review: Reason for weight monitoring:</p>,
    type: 'radio',
    name: 'reasonForWeightMonitoring',
    required: false,
    options: [
      {
        key: makeid(10),
        label: 'Congestive heart failure (CHF)',
        value: 'congestive heart failure (CHF)',
      },
      {
        key: makeid(10),
        label: 'Renal disease',
        value: 'renal disease',
      },
      {
        key: makeid(10),
        label: 'Weight management',
        value: 'weight management',
      },
      {
        key: makeid(10),
        label: 'Other (specify)',
        value: 'other',
        hideSubitemsIfParentNotSelected: true,
        subItems: [
          {
            key: makeid(10),
            title: null,
            type: 'input',
            name: 'reasonForWeightMonitoringOther',
            required: false,
          },
        ],
      },
    ],
  },
  {
    key: makeid(10),
    title: <p>Chart Review: At-risk trend summary:</p>,
    type: 'empty',
    name: '_empty',
    items: [
      {
        key: makeid(10),
        title: (
          <p>
            Highest weight: <span>__ lbs/kg</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'highestWeight',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Lowest weight: <span>__ lbs/kg</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'lowestWeight',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: (
          <p>
            Average weight this week: <span>__ lbs/kg</span>
          </p>
        ),
        type: 'input-number',
        required: false,
        name: 'avgWeight',
        validation: { min: 0 },
      },
      {
        key: makeid(10),
        title: <p>Readings are trending:</p>,
        type: 'radio',
        required: false,
        name: 'typeofTrend',
        options: [
          {
            label: 'High',
            value: 'high',
            key: makeid(10),
          },
          {
            label: 'Low',
            value: 'low',
            key: makeid(10),
          },
          {
            label: 'Both',
            value: 'high&low',
            key: makeid(10),
          },
          {
            label: 'Fluctuating',
            value: 'fluctuating',
            key: makeid(10),
          },
        ],
      },
      {
        key: makeid(10),
        title: null,
        name: 'typeofTrendComment',
        type: 'textarea',
      },
    ],
  },
  { ...nurseContactWithPatientItem },
  { ...nurseNoCallMadeRationaleItem },
  {
    key: makeid(10),
    title: <p>Clinical Assessment</p>,
    type: 'empty',
    name: '_empty',
    items: [
      ...clinicalAssessmentItems,
      {
        key: makeid(10),
        title: <p>Other acute concerns:</p>,
        type: 'textarea',
        name: 'clinicalAssessmentComment',
      },
    ],
  },
  { ...nurseReviewEscalationProtocolItem },
  { ...nurseEmergencyCareAdvisedItem },
  {
    key: makeid(10),
    title: <p>Potential contributing factors:</p>,
    type: 'checkbox',
    required: false,
    name: 'potentialContributingFactors',
    options: [
      {
        label: 'Non-adherence to meds or diuretics',
        value: 'non-adherence to meds or diuretics',
        key: makeid(10),
      },
      {
        label: 'Dietary indiscretion (salt/fluid)',
        value: 'dietary indiscretion (salt/fluid)',
        key: makeid(10),
      },
      {
        label: 'Device/measurement issue',
        value: 'device/measurement issue',
        key: makeid(10),
      },
      {
        label: 'Illness/infection',
        value: 'illness/infection',
        key: makeid(10),
      },
      {
        label: 'Activity/inactivity',
        value: 'activity/inactivity',
        key: makeid(10),
      },
      {
        label: 'No identifiable cause',
        value: 'no identifiable cause',
        key: makeid(10),
      },
    ],
    items: [
      {
        key: makeid(10),
        title: null,
        type: 'textarea',
        name: 'potentialContributingFactorsComment',
      },
    ],
  },
  { ...nurseDecisionMakingNextStepsItem },
  {
    key: makeid(10),
    type: 'checkbox',
    title: <p>Patient education provided:</p>,
    name: 'patientEducationProvided',
    required: false,
    options: [
      {
        label: 'Medication adherence—especially diuretics',
        value: 'medication adherence—especially diuretics',
        key: makeid(10),
      },
      {
        label: 'Daily weight measurement technique',
        value: 'daily weight measurement technique',
        key: makeid(10),
      },
      {
        label: 'Sodium/fluid restriction',
        value: 'sodium/fluid restriction',
        key: makeid(10),
      },
      {
        label: 'Warning signs—when to call',
        value: 'warning signs—when to call',
        key: makeid(10),
      },
      {
        label: 'Activity/exercise guidance',
        value: 'activity/exercise guidance',
        key: makeid(10),
      },
    ],
    items: [
      {
        key: makeid(10),
        title: null,
        type: 'textarea',
        name: 'patientEducationProvidedComment',
      },
    ],
  },
  { ...nurseDigitalEngagementEducationMethodItem },
  { ...nurseMaTasksDelegatedItem },
  {
    key: makeid(10),
    type: 'textarea',
    title: <p>Nurse clinical summary and rationale:</p>,
    name: 'nurseClinicalSummaryAndRationale',
    required: false,
  },
  { ...nurseFollowUpPlanItem },
  { ...nursePatientAcknowledgementItem },
  { ...nursePatientAgreeabilityItem },
];
